/**
 * Таблица строк плана «культура по предшественнику» (макет v2-04).
 *
 * Почему строка — не поле: технология одинакова для всех полей с одной
 * культурой и одним предшественником, а полей у хозяйства две сотни —
 * назначать техкарту на каждое означало бы двести одинаковых действий.
 * Культуры по полям раскладываются в другом экране, здесь на готовую
 * структуру вешаются технологии.
 */
import { Fragment } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  formatHa,
  formatMln,
  formatRubPerHa,
  groupRowsByCrop,
  rowTitle,
  type PlanRow,
} from '@/lib/planning/planVariant';
import { cn } from '@/lib/utils';

export function PlanRowsTable({
  rows,
  arableHa,
  selectedKey,
  onSelect,
}: {
  rows: PlanRow[];
  arableHa: number;
  selectedKey: string | null;
  onSelect: (row: PlanRow) => void;
}) {
  const groups = groupRowsByCrop(rows, arableHa);
  const calculated = rows.filter((r) => r.state === 'assigned');
  const totalArea = calculated.reduce((acc, r) => acc + r.area_ha, 0);
  const totalCost = calculated.reduce((acc, r) => acc + (r.cost_total ?? 0), 0);

  return (
    <Card>
      <CardContent className="p-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Строка плана: культура по предшественнику</TableHead>
              <TableHead className="text-right">Площадь, га</TableHead>
              <TableHead className="text-right">Урожайность, ц/га</TableHead>
              <TableHead>Вариант техкарты</TableHead>
              <TableHead className="text-right">Затраты, ₽/га</TableHead>
              <TableHead className="text-right">Затраты</TableHead>
              <TableHead>Состояние</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((group) => (
              <Fragment key={group.title}>
                <TableRow className="bg-muted/60 hover:bg-muted/60">
                  <TableCell colSpan={7} className="font-medium text-foreground">
                    {group.title} · {formatHa(group.area_ha)}
                    {group.share_pct !== null && ` · ${Math.round(group.share_pct)} % пашни`}
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      {group.assigned} из {group.rows.length} строк назначено
                    </span>
                  </TableCell>
                </TableRow>
                {group.rows.map((row) => (
                  <TableRow
                    key={row.key}
                    onClick={() => onSelect(row)}
                    aria-selected={row.key === selectedKey}
                    className={cn(
                      'cursor-pointer',
                      row.key === selectedKey && 'bg-primary/5',
                      row.state === 'unassigned' && 'bg-accent-brand/5',
                    )}
                  >
                    <TableCell>
                      <div className="text-foreground">{rowTitle(row)}</div>
                      <div className="text-xs text-muted-foreground">
                        {row.fields_count} {pluralFields(row.fields_count)}
                      </div>
                      {row.gaps.length > 0 && (
                        <div className="mt-1 text-xs text-accent-brand">{row.gaps.join(' · ')}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Math.round(row.area_ha).toLocaleString('ru-RU')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {row.yield_c_ha === null ? '—' : Math.round(row.yield_c_ha)}
                    </TableCell>
                    <TableCell>
                      {row.tech_card ? (
                        <>
                          <div className="text-foreground">{row.tech_card.name}</div>
                          {!row.tech_card.exact_predecessor && (
                            <div className="text-xs text-muted-foreground">общий вариант культуры</div>
                          )}
                        </>
                      ) : row.state === 'assigned' || row.state === 'unassigned' ? (
                        <span className="text-accent-brand">выбрать вариант</span>
                      ) : (
                        <span className="text-muted-foreground">техкарта неприменима</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.cost_per_ha === null ? '—' : formatRubPerHa(row.cost_per_ha)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatMln(row.cost_total)}</TableCell>
                    <TableCell>
                      <StateBadge row={row} />
                    </TableCell>
                  </TableRow>
                ))}
              </Fragment>
            ))}

            <TableRow className="bg-muted/60 hover:bg-muted/60">
              <TableCell className="font-semibold text-foreground">Итого назначено</TableCell>
              <TableCell className="text-right font-semibold tabular-nums">
                {Math.round(totalArea).toLocaleString('ru-RU')}
              </TableCell>
              <TableCell className="text-right text-muted-foreground">—</TableCell>
              <TableCell className="text-muted-foreground">
                {new Set(calculated.map((r) => r.tech_card?.id)).size} вариантов техкарт
              </TableCell>
              <TableCell className="text-right font-semibold tabular-nums">
                {totalArea > 0 ? formatRubPerHa(totalCost / totalArea) : '—'}
              </TableCell>
              <TableCell className="text-right font-semibold tabular-nums">{formatMln(totalCost)}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {rows.some((r) => r.state === 'unassigned')
                  ? 'без строк, у которых нет варианта техкарты'
                  : 'все строки под культурами посчитаны'}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <p className="mt-3 text-xs leading-5 text-muted-foreground">
          <b className="text-foreground">Почему строка — это «культура по предшественнику», а не поле.</b>{' '}
          Технология одинакова для всех полей с одной культурой и одним предшественником. Разложить
          культуры по конкретным полям можно в распределении культур по полям — здесь на готовую
          структуру вешаются технологии.
        </p>
      </CardContent>
    </Card>
  );
}

function StateBadge({ row }: { row: PlanRow }) {
  if (row.state === 'assigned') {
    const changed =
      row.tech_card?.changed_at && row.tech_card.assigned_at && row.tech_card.changed_at > row.tech_card.assigned_at;
    return changed ? (
      <Badge variant="accent-brand">техкарта менялась</Badge>
    ) : (
      <Badge variant="primary">назначен</Badge>
    );
  }
  if (row.state === 'unassigned') return <Badge variant="destructive">без техкарты</Badge>;
  if (row.state === 'no_crop') return <Badge variant="destructive">нет культуры</Badge>;
  return <Badge variant="outline">без культуры</Badge>;
}

function pluralFields(count: number): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return 'поле';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'поля';
  return 'полей';
}
